import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BoundedApiError, type Parameter } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { ErrorNotice } from '@bounded/ui/state';
import { Button, Input, Panel, SectionHeader, Select, SettingRow, Skeleton } from '@bounded/ui/primitives';
import { ParameterControl } from '../../components/policy/parameter-control';
import { ChoosePolicy } from '../../components/policy/source';
import { PublishRail } from '../../components/policy/publish-rail';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';
import { useDraft } from '../../lib/draft';
import { cn } from '@bounded/ui/cn';
import { pickLocalized, useLocale, useT } from '../../i18n';

const TASK_ID = /^[a-z][a-z0-9_-]{0,95}$/;
export const Route = createFileRoute('/_app/projects/$projectId/policy')({
  // `mine` edits your account-wide defaults; `task` edits one task's overrides; neither = this project.
  validateSearch: (s): { task?: string; mine?: true } => ({
    task: typeof s.task === 'string' && TASK_ID.test(s.task) ? s.task : undefined,
    mine: s.mine === true || s.mine === 'true' ? true : undefined,
  }),
  // Prefetch, don't ensure: a new project has no draft (404), which is a state to render, not an error page.
  loader: ({ context, params }) => { void context.queryClient.prefetchQuery(q.draft(params.projectId)); },
  component: PolicyPage,
});

type Scope = 'default' | 'personal' | 'project' | 'task';

/** Effective value and where it came from, resolved the way graph-core does: default → personal → project → task. */
function resolve(id: string, p: Parameter, layers: [Scope, Record<string, unknown> | undefined][]): { value: unknown; source: Scope } {
  let value: unknown = p.default; let source: Scope = 'default';
  for (const [scope, values] of layers) if (values && id in values) { value = values[id]; source = scope; }
  return { value, source };
}

function PolicyPage() {
  const t = useT();
  const { projectId } = Route.useParams();
  const draft = useQuery(q.draft(projectId));
  const [replacing, setReplacing] = useState(false);
  // Bumped when the draft is replaced wholesale, so the editor remounts with the new source instead of its old working copy.
  const [generation, setGeneration] = useState(0);
  const missing = draft.error instanceof BoundedApiError && draft.error.code === 'DRAFT_NOT_FOUND';

  if (missing) return (
    <div className="grid gap-6">
      <SectionHeader title={t.policy.chooseTitle} description={t.policy.chooseDescription} />
      <ChoosePolicy projectId={projectId} revision={0} onDone={() => void draft.refetch()} />
    </div>
  );
  if (draft.error) return <ErrorNotice error={draft.error} onRetry={() => void draft.refetch()} />;
  if (!draft.data) return <Skeleton className="h-96" />;
  if (replacing) return <ChoosePolicy projectId={projectId} revision={draft.data.revision} onDone={() => { setReplacing(false); setGeneration((g) => g + 1); }} onCancel={() => setReplacing(false)} />;
  return <PolicyEditor key={generation} onReplace={() => setReplacing(true)} />;
}

function PolicyEditor({ onReplace }: { onReplace: () => void }) {
  const t = useT(); const m = t.policy;
  const { locale } = useLocale();
  const { projectId } = Route.useParams();
  const { task, mine } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  // Working copy + CAS autosave shared with the graph canvas (lib/draft.ts).
  const { draft, bundle, base, save, edit, reload } = useDraft(projectId);
  const seenTasks = useQuery(q.tasks(projectId));
  const graphId = draft.data!.source.graph.graph_id;
  const defaults = useQuery(q.defaults(graphId));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newTask, setNewTask] = useState('');

  // Personal defaults are account-wide, so they are saved explicitly rather than autosaved.
  const [personal, setPersonal] = useState<Record<string, unknown> | null>(null);
  useEffect(() => { if (defaults.data && personal === null) setPersonal(structuredClone(defaults.data.parameters)); }, [defaults.data, personal]);
  const personalDirty = !!defaults.data && personal !== null && JSON.stringify(personal) !== JSON.stringify(defaults.data.parameters);
  const savePersonal = useMutation({
    mutationFn: () => api.saveDefaults(graphId, defaults.data!.revision, personal!),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['policy-defaults', graphId] }); await queryClient.invalidateQueries({ queryKey: ['project', projectId] }); setPersonal(null); },
  });
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (personalDirty) e.preventDefault(); };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [personalDirty]);

  const models = useQuery(q.models());
  const hosted = useQuery(q.hostedModels());
  const connectionName = (id: string) => models.data?.items.find((x) => x.id === id)?.name ?? (hosted.data?.items.some((h) => h.id === id) ? t.models.providers.hosted : id);

  if (!bundle || personal === null) return <Skeleton className="h-96" />;
  const scope: Scope = mine ? 'personal' : task ? 'task' : 'project';
  const layers: [Scope, Record<string, unknown> | undefined][] = scope === 'personal' ? [['personal', personal]]
    : [['personal', defaults.data?.parameters], ['project', bundle.overrides.project], ...(task ? [['task', bundle.overrides.tasks[task]] as [Scope, Record<string, unknown> | undefined]] : [])];
  const params = Object.entries(bundle.graph.parameters);
  const visible = params.filter(([, p]) => showAdvanced || !p.advanced);
  const advancedCount = params.filter(([, p]) => p.advanced).length;
  // Scopes: tasks that already have overrides, plus tasks devices have actually asked about.
  const overridden = Object.keys(bundle.overrides.tasks);
  const seen = [...new Set((seenTasks.data?.items ?? []).filter((x) => !x.closed_at).map((x) => x.task_id))].filter((x) => !overridden.includes(x));
  const locked = save.state === 'conflict' || (scope === 'personal' && savePersonal.isPending);

  const setValue = (id: string, value: unknown) => {
    if (scope === 'personal') return setPersonal({ ...personal, [id]: value });
    const next = structuredClone(bundle);
    if (scope === 'project') next.overrides.project[id] = value; else (next.overrides.tasks[task!] ??= {})[id] = value;
    edit(next);
  };
  const reset = (id: string) => {
    if (scope === 'personal') { const rest = { ...personal }; delete rest[id]; return setPersonal(rest); }
    const next = structuredClone(bundle);
    if (scope === 'project') delete next.overrides.project[id]; else delete next.overrides.tasks[task!]?.[id];
    edit(next);
  };
  const addTask = () => {
    if (!TASK_ID.test(newTask)) return;
    if (!bundle.overrides.tasks[newTask]) { const next = structuredClone(bundle); next.overrides.tasks[newTask] = {}; edit(next); }
    void navigate({ search: { task: newTask } }); setNewTask('');
  };
  const scopeValue = mine ? '__mine' : task ?? '';

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_18rem] lg:items-start">
      <Panel
        footer={(advancedCount > 0 || scope !== 'personal') && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            {advancedCount > 0
              ? <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-sm text-graphite hover:text-ink">{showAdvanced ? m.hideAdvanced : m.showAdvanced(advancedCount)}</button>
              : <span />}
            {scope !== 'personal' && (
              <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); addTask(); }}>
                <Input aria-label={m.taskId} placeholder={m.taskId} className="h-7 w-36 font-mono text-sm" value={newTask} onChange={(e) => setNewTask(e.target.value.trim())} />
                <Button size="sm" type="submit" disabled={!TASK_ID.test(newTask)}>{m.overrideTask}</Button>
              </form>
            )}
          </div>
        )}
        title={m.settings}
        description={scope === 'personal' ? m.descMine(<code>{graphId}</code>) : scope === 'project' ? m.descProject : m.descTask(<code>{task}</code>)}
        actions={
          <Select aria-label={m.scopeLabel} className="w-full sm:w-56" value={scopeValue}
            onChange={(e) => void navigate({ search: e.target.value === '__mine' ? { mine: true } : { task: e.target.value || undefined } })}>
            <option value="">{m.scopeProject}</option>
            {overridden.map((x) => <option key={x} value={x}>{m.scopeTask(x)}</option>)}
            {task && !overridden.includes(task) && !seen.includes(task) && <option value={task}>{m.scopeTask(task)}</option>}
            {seen.length > 0 && <optgroup label={m.seenTasks}>{seen.map((x) => <option key={x} value={x}>{m.scopeTask(x)}</option>)}</optgroup>}
            <option value="__mine">{m.scopeMine}</option>
          </Select>
        }>
        {/* The values set at this scope, as JSON. Inherited values aren't part of it: they belong to the scope that sets them. */}
        <ConfigEditor key={scopeValue} id="settings-json" inset commit="apply" rows={12} disabled={locked}
          value={scope === 'personal' ? personal : scope === 'project' ? bundle.overrides.project : bundle.overrides.tasks[task!] ?? {}}
          validate={(v) => { if (!isObject(v)) return t.configEditor.notObject; const unknown = Object.keys(v).find((k) => !(k in bundle.graph.parameters)); return unknown ? m.unknownSetting(unknown) : null; }}
          onChange={(values) => {
            if (scope === 'personal') return setPersonal(values as Record<string, unknown>);
            const next = structuredClone(bundle);
            if (scope === 'project') next.overrides.project = values as Record<string, unknown>; else next.overrides.tasks[task!] = values as Record<string, unknown>;
            edit(next);
          }}>
        <div>
          {visible.map(([id, p]) => {
            const { value, source } = resolve(id, p, layers);
            const setHere = source === scope;
            // Label and description may ship translations; the parameter id and value never change with language.
            const text = pickLocalized({ label: p.label, description: p.description }, p.i18n, locale);
            return (
              <SettingRow key={id} wide htmlFor={`p-${id}`} label={text.label} description={text.description}
                meta={setHere
                  ? <span className="inline-flex flex-wrap items-center gap-2"><span className="rounded bg-boundary/10 px-1.5 text-boundary">{m.setFor(m.scopes[scope])}</span><button type="button" onClick={() => reset(id)} className="text-graphite underline-offset-2 hover:text-ink hover:underline">{m.reset}</button></span>
                  : <span className="text-graphite">{m.inheritedFrom(m.scopes[source])}</span>}
                control={<div className={cn(!setHere && 'opacity-80')}><ParameterControl id={`p-${id}`} p={p} value={value} onChange={(v) => setValue(id, v)} connectionName={connectionName} disabled={locked} /></div>} />
            );
          })}
          {visible.length === 0 && <p className="px-4 py-6 text-graphite">{m.noSettings}</p>}
        </div>
        </ConfigEditor>
      </Panel>

      {scope === 'personal' ? (
        <aside className="lg:sticky lg:top-8">
          <Panel>
            <div className="grid gap-3 p-4">
              <p className="font-medium">{personalDirty ? m.mineUnsaved : m.mineSaved(defaults.data?.revision ?? 0)}</p>
              <p className="text-sm text-graphite">{m.mineNote}</p>
              {savePersonal.error && <ErrorNotice error={savePersonal.error} onRefresh={() => { setPersonal(null); savePersonal.reset(); void defaults.refetch(); }} />}
              <Button variant="primary" disabled={!personalDirty || savePersonal.isPending} onClick={() => savePersonal.mutate()}>{m.saveMine}</Button>
              {personalDirty && <Button variant="ghost" onClick={() => setPersonal(structuredClone(defaults.data!.parameters))}>{m.discard}</Button>}
            </div>
          </Panel>
        </aside>
      ) : (
        <PublishRail projectId={projectId} save={save} base={base} defaultsRevision={draft.data!.defaults_revision} task={task} onReplace={onReplace} onReload={reload} />
      )}
    </div>
  );
}
